import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { MapPin, Phone, Mail, Camera } from "lucide-react";
import { useState, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { sendNotification } from "@/lib/emailService";
import { useTranslation } from "react-i18next";

import { LocalizedContent } from "@/types";
import { useLocalized } from "@/hooks/useLocalized";

interface Profile {
  display_name: string;
  location?: LocalizedContent;
  phone?: string;
  email?: string;
}

export function ContactSection() {
  const { t } = useTranslation();
  const { getLocalized } = useLocalized();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });
  const { toast } = useToast();

  useEffect(() => {
    const fetchProfile = async () => {
      const { data, error } = await supabase
        .from("photographer_profiles")
        .select("display_name, location, phone, email")
        .single();

      if (error) {
        console.error("Error fetching profile:", error);
        return;
      }

      setProfile(data);
    };

    fetchProfile();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Synchronously open window to bypass popup blockers
    let whatsappWindow: Window | null = null;
    if (profile?.phone) {
      whatsappWindow = window.open('', '_blank');
      if (whatsappWindow) {
        whatsappWindow.document.write('Loading WhatsApp...');
      }
    }

    try {
      // 1. Save message to database (Admin Panel)
      const { error: dbError } = await supabase
        .from("contact_messages")
        .insert({
          name: formData.name,
          email: formData.email,
          phone: formData.phone || null,
          message: formData.message,
        });

      if (dbError) throw dbError;

      // 2. Redirect opened window to WhatsApp
      if (whatsappWindow && profile?.phone) {
        const whatsappMessage = `*New Inquiry via Website* %0A%0A*Name:* ${formData.name}%0A*Email:* ${formData.email}%0A*Phone:* ${formData.phone || 'N/A'}%0A*Message:* ${formData.message}`;
        const cleanPhone = profile.phone.replace(/\D/g, '');
        const whatsappUrl = `https://wa.me/${cleanPhone}?text=${whatsappMessage}`;

        whatsappWindow.location.href = whatsappUrl;
      }

      // 3. Send email notification
      await sendNotification(
        `New Message from ${formData.name}`,
        "New Contact Message",
        `
        <div class="info-box">
             <div class="info-label">Sender</div>
             <div class="info-value" style="font-size:16px;">${formData.name}</div>
        </div>

        <div class="section-title">Contact Details</div>
        <table width="100%">
            <tr>
                <td width="50%" class="info-row">
                    <span class="info-label">Email</span>
                    <div class="info-value"><a href="mailto:${formData.email}" style="color:#111;text-decoration:none;">${formData.email}</a></div>
                </td>
                <td width="50%" class="info-row">
                    <span class="info-label">Phone</span>
                    <div class="info-value">${formData.phone || 'N/A'}</div>
                </td>
            </tr>
        </table>
        
        <div class="section-title">Message Bubble</div>
        <div style="background:#eef2ff; border:1px solid #dbeafe; padding:20px; border-radius:12px; border-top-left-radius:0; color:#1e3a8a; font-size:15px; line-height:1.6; margin-left:10px;">
           ${formData.message.replace(/\n/g, '<br/>')}
        </div>
        `,
        formData.email,
        `${window.location.origin}/admin`,
        "View Messages"
      );

      toast({
        title: t('contact.successTitle', "Success!"),
        description: t('contact.successMessage', "Message sent successfully!"),
      });

      setFormData({ name: "", email: "", phone: "", message: "" });
    } catch (error: any) {
      console.error("Submission error:", error);

      // Close the window if it was opened but operation failed
      if (whatsappWindow) whatsappWindow.close();

      toast({
        title: t('contact.errorTitle', "Error"),
        description: error.message || t('contact.errorMessage', "Failed to send message. Please try again."),
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="py-12 sm:py-16 md:py-20 px-4 bg-muted/30">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-8 sm:mb-12 md:mb-16">
          <div className="flex items-center justify-center gap-2 sm:gap-3 mb-4 sm:mb-6">
            <Camera className="h-8 w-8 sm:h-10 sm:w-10 text-primary" />
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold">{t('contact.title', 'Get In Touch')}</h2>
          </div>
          <p className="text-base sm:text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto px-4">
            {t('contact.subtitle', "Ready to capture your special moments? Let's discuss your photography needs")}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-10 md:gap-12">
          {/* Contact Info */}
          <div className="space-y-8">
            <div>
              <h3 className="text-2xl font-semibold mb-6">{t('contact.infoTitle', 'Contact Information')}</h3>
              <div className="space-y-4">
                <div className="flex items-center gap-4 p-4 rounded-lg bg-background/50 backdrop-blur-sm">
                  <MapPin className="h-5 w-5 text-primary flex-shrink-0" />
                  <div>
                    <p className="font-medium">{t('contact.location', 'Location')}</p>
                    <p className="text-muted-foreground">{getLocalized(profile?.location) || "Location"}</p>
                  </div>
                </div>

                <div className="flex items-center gap-4 p-4 rounded-lg bg-background/50 backdrop-blur-sm">
                  <Phone className="h-5 w-5 text-primary flex-shrink-0" />
                  <div>
                    <p className="font-medium">{t('contact.phone', 'Phone')}</p>
                    <p className="text-muted-foreground">{profile?.phone || "Phone"}</p>
                  </div>
                </div>

                <div className="flex items-center gap-4 p-4 rounded-lg bg-background/50 backdrop-blur-sm">
                  <Mail className="h-5 w-5 text-primary flex-shrink-0" />
                  <div>
                    <p className="font-medium">{t('contact.email', 'Email')}</p>
                    <p className="text-muted-foreground">{profile?.email || "Email"}</p>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-2xl font-semibold mb-4">{t('contact.whyChooseUs', 'Why Choose')} {profile?.display_name || "Us"}?</h3>
              <ul className="space-y-3 text-muted-foreground">
                <li className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0"></div>
                  <span>{t('contact.reason1', 'Professional equipment and extensive experience')}</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0"></div>
                  <span>{t('contact.reason2', 'Personalized approach to every shoot')}</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0"></div>
                  <span>{t('contact.reason3', 'Quick turnaround and high-quality results')}</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0"></div>
                  <span>{t('contact.reason4', 'Flexible packages to fit your budget')}</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Contact Form */}
          <Card className="shadow-[var(--shadow-elegant)]">
            <CardContent className="p-4 sm:p-6 md:p-8">
              <h3 className="text-xl sm:text-2xl font-semibold mb-4 sm:mb-6">{t('contact.formTitle', 'Send us a Message')}</h3>
              <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="name">{t('contact.name', 'Name')}</Label>
                  <Input
                    id="name"
                    name="name"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">{t('contact.email', 'Email')}</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone">{t('contact.phoneOptional', 'Phone (Optional)')}</Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message">{t('contact.message', 'Message')}</Label>
                  <Textarea
                    id="message"
                    name="message"
                    placeholder={t('contact.messagePlaceholder', "Tell us about your photography needs...")}
                    rows={4}
                    required
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full"
                  size="lg"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? t('contact.sending', "Sending...") : t('contact.send', "Send Message")}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
}